import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Fixed left sidebar with anchor navigation.
 *
 * Uses `position: fixed` (not sticky) because the DashboardContent parent has
 * `overflow-y-auto` which creates a scroll containing block, breaking sticky.
 * A spacer div maintains the layout column; the inner <aside> is fixed-positioned
 * with its left coordinate computed from the spacer on mount/resize.
 *
 * Active section is highlighted via IntersectionObserver.
 *
 * @param {Object} props
 * @param {Array<{id: string, title: string}>} props.sections
 */
export function Sidebar({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const [leftPx, setLeftPx] = useState(0);
  const spacerRef = useRef(null);

  // Track scroll position to highlight active section
  useEffect(() => {
    const elements = sections.map(s => document.getElementById(s.id)).filter(Boolean);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  // Compute fixed left coordinate from the spacer position (and on resize)
  useEffect(() => {
    const updateLeft = () => {
      if (spacerRef.current) {
        const rect = spacerRef.current.getBoundingClientRect();
        setLeftPx(rect.left);
      }
    };
    updateLeft();
    window.addEventListener('resize', updateLeft);
    return () => window.removeEventListener('resize', updateLeft);
  }, []);

  return (
    <div
      ref={spacerRef}
      className="hidden w-[220px] shrink-0 lg:block"
    >
      <aside
        className="fixed top-[120px] z-10 max-h-[calc(100vh-140px)] w-[220px] overflow-y-auto pr-4"
        style={{ left: leftPx }}
      >
        <nav>
          <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-neutral-dark">Navegação</h3>
          <ul className="space-y-1">
            {sections.map(section => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={cn('block rounded-md px-3 py-2 text-sm font-bold transition-colors', activeId === section.id ? 'bg-primary-lightest text-primary' : 'text-neutral-dark hover:bg-neutral-50 hover:text-primary')}
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </div>
  );
}
